import { Subject, of } from 'rxjs'
import { takeUntil, catchError } from 'rxjs/operators'

import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { OpsLaborEntry, OpsService } from '@services/ops.service'
import { ConfirmClockModalComponent, ConfirmClockData } from '../../../features/oee-dashboard/components/modals/confirm-clock-modal/confirm-clock-modal.component'
import { EditShiftModalComponent, EditShiftModalResult } from '../edit-shift-modal/edit-shift-modal.component'

@Component({
    selector: 'shared-clock-in-out',
    templateUrl: './clock-in-out.component.html',
    styleUrls: ['./clock-in-out.component.css'],
    standalone: false
})
export class ClockInOutComponent implements OnInit, OnDestroy {
    @Input() siteId: string | null = null

    activeEntry: OpsLaborEntry | null = null
    elapsedDisplay = ''
    loading = false

    private destroy$ = new Subject<void>()
    private timerInterval: ReturnType<typeof setInterval> | null = null

    constructor(
        private _opsService: OpsService,
        private _cdr: ChangeDetectorRef,
        private _dialog: MatDialog
    ) {}

    ngOnInit(): void {
        // If no siteId was passed as input, fetch sites and auto-select
        if (!this.siteId) {
            this._opsService.getSites().pipe(
                takeUntil(this.destroy$),
                catchError(() => of([]))
            ).subscribe(sites => {
                if (sites.length > 0) {
                    // Auto-select first site (most orgs have one site)
                    this.siteId = sites[0].id
                }
                this.checkActiveClockIn()
                this._cdr.detectChanges()
            })
        } else {
            this.checkActiveClockIn()
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
        this.clearTimer()
    }

    get isClockedIn(): boolean {
        return this.activeEntry !== null
    }

    /** Handle clock in/out button click */
    onToggleClock(): void {
        if (this.loading) return

        if (this.isClockedIn) {
            this.handleClockOut()
        } else {
            this.handleClockIn()
        }
    }

    private checkActiveClockIn(): void {
        this.loading = true
        this._opsService.getActiveClockIn()
            .pipe(
                takeUntil(this.destroy$),
                catchError(() => of(null))
            )
            .subscribe(entry => {
                this.activeEntry = entry
                if (entry) {
                    // If we got an active entry, use its site_id as fallback
                    if (!this.siteId) {
                        this.siteId = entry.site_id
                    }
                    this.startTimer()
                }
                this.loading = false
                this._cdr.detectChanges()
            })
    }

    private handleClockIn(): void {
        if (!this.siteId) return

        const dialogRef = this._dialog.open(ConfirmClockModalComponent, {
            width: '450px',
            panelClass: 'oee-mobile-modal',
            data: { mode: 'clock-in' } as ConfirmClockData
        })

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return

            this.loading = true
            this._cdr.detectChanges()
            this._opsService.clockIn({ site_id: this.siteId! })
                .pipe(
                    takeUntil(this.destroy$),
                    catchError(() => {
                        this.loading = false
                        this._cdr.detectChanges()
                        return of(null)
                    })
                )
                .subscribe(entry => {
                    if (entry) {
                        this.activeEntry = entry
                        this.startTimer()
                    }
                    this.loading = false
                    this._cdr.detectChanges()
                })
        })
    }

    private handleClockOut(): void {
        if (!this.activeEntry) return

        const dialogRef = this._dialog.open(EditShiftModalComponent, {
            width: '600px',
            maxWidth: '95vw',
            panelClass: 'edit-shift-dialog',
            data: {
                entry: this.activeEntry,
                siteId: this.siteId ?? this.activeEntry.site_id
            }
        })

        dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: EditShiftModalResult | undefined) => {
            if (result?.clockedOut || result?.deleted) {
                this.activeEntry = null
                this.clearTimer()
                this.elapsedDisplay = ''
                this._cdr.detectChanges()
            } else if (result?.edited) {
                // Re-fetch active entry in case clock-in time was changed
                this.checkActiveClockIn()
            }
        })
    }

    private startTimer(): void {
        this.clearTimer()
        this.updateElapsed()
        this.timerInterval = setInterval(() => {
            this.updateElapsed()
            this._cdr.detectChanges()
        }, 60_000)
    }

    private clearTimer(): void {
        if (this.timerInterval !== null) {
            clearInterval(this.timerInterval)
            this.timerInterval = null
        }
    }

    private updateElapsed(): void {
        this.elapsedDisplay = this.computeElapsed()
    }

    private computeElapsed(): string {
        if (!this.activeEntry) return ''
        const clockInTime = new Date(this.activeEntry.clock_in_ts).getTime()
        const now = Date.now()
        const diffMs = now - clockInTime
        const totalMinutes = Math.floor(diffMs / 60_000)
        const hours = Math.floor(totalMinutes / 60)
        const minutes = totalMinutes % 60
        if (hours > 0) {
            return `${hours}h ${minutes}m`
        }
        return `${minutes}m`
    }
}
